import {
  Banner,
  FlexBox,
  Modal,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import groupBy from 'lodash/groupBy';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { AppLink } from 'components/appLink';
import { CommandGroup, useCommands } from 'utils/commands';
import { createAppModal } from 'utils/modal';

import { KeybindingGroup } from './components/group/keybindingGroup';
import styles from './keyboardCheatSheetModal.module.scss';

export const KeyboardCheatSheetModal = createAppModal(
  {
    type: 'app:keyboardCheatSheet',
  },
  ({ isOpen, hide }): JSX.Element => {
    const { pathname } = useLocation();
    const { enrichedCommands, hasKeybindingsConflicts } = useCommands();

    const isOnSettingsPage = pathname === '/settings/keyboard-shortcuts';

    const cheatSheet = useMemo(() => {
      const groupedCommands = groupBy(
        enrichedCommands.filter(
          ({ command }) => !!command.keyboard?.keybindings.length,
        ),
        ({ command: { group } }) => group,
      );

      return (
        <FlexBox gap="500" flexDirection="column">
          {Object.entries(groupedCommands).map(([group, enrichedCommands]) => (
            <KeybindingGroup
              key={group}
              group={group as CommandGroup}
              enrichedCommands={enrichedCommands}
            />
          ))}
        </FlexBox>
      );
    }, [enrichedCommands]);

    return (
      <Modal
        isOpen={isOpen}
        className={styles.modal}
        allowClickOutside
        allowEscClose
        onClose={hide}
      >
        <ModalHead title="Keyboard cheat sheet" onClose={hide} />
        <ModalBody>
          <FlexBox
            ref={(element) => (element as any)?.focus()}
            gap="500"
            flexDirection="column"
            tabIndex={-1}
          >
            {!isOnSettingsPage && (
              <Text.Paragraph variant="2">
                You have enabled keyboard shortcuts. You can customize or
                unassign them from the{' '}
                <AppLink
                  path={'/settings/preferences/keyboard-shortcuts'}
                  onClick={hide}
                >
                  settings page
                </AppLink>
                .
              </Text.Paragraph>
            )}

            {hasKeybindingsConflicts && (
              <Banner
                variant="warning"
                description={
                  <Text.Paragraph variant="4">
                    There are conflicting keybindings in the configuration.
                    Please resolve if you wish to use the conflicting commands.
                  </Text.Paragraph>
                }
              />
            )}

            {cheatSheet}
          </FlexBox>
        </ModalBody>
      </Modal>
    );
  },
);
